import {html, LitElement} from '@isceco/widget-library2/external/lit'
import {ref} from '@isceco/widget-library2/external/lit/directives/ref.js'
import Plotly from 'plotly.js-dist-min'

export class GasConsumptionPlot extends LitElement {

  static properties = {
    i18n: {type: Object},
    source: {type: String},
    region: {type: String},
    dates: {type: Array},
    temperature: {type: Array},
    gasConsumption: {type: Array},
    gasConsumptionPrognose: {type: Array},
    gasConsumptionMlPrognose: {type: Array},
    gasConsumptionMlPrognoseVisible: {type: Boolean},
    gebKapazitaet: {type: Array},
    angebot: {type: Array},
    regAngebot: {type: Array},
    nomKapazitaet: {type: Array},
    gasConsumptionPrognoseMitMassnahmen: {type: Array},
  }

  static PLOT_CONFIG = {
    SCATTER_TYPE: 'scatter',
    LINES_MARKERS_MODE: 'lines+markers',
    LINES_MODE: 'lines'
  }

  constructor() {
    super()
    this.i18n = {}
    this.dates = []
    this.gasConsumption = []
    this.temperature = []
    this.gasConsumptionPrognose = []
    this.gasConsumptionMlPrognose = []
    this.gebKapazitaet = []
    this.angebot = []
    this.regAngebot = []
    this.nomKapazitaet = []
    this.gasConsumptionPrognoseMitMassnahmen = []
  }

  render() {
    // Sort all data
    const [sortedDates, sortedGasConsumption, sortedPrognose, sortedPrognoseMl, sortedGebKapa, sortedNomKapa, sortedPrognoseMass, sortedAngebot, sortedRegAngebot] =
      this._sortData(this.dates, this.gasConsumption, this.gasConsumptionPrognose, this.gasConsumptionMlPrognose, this.gebKapazitaet,
        this.nomKapazitaet, this.gasConsumptionPrognoseMitMassnahmen, this.angebot, this.regAngebot);

    this.dates = sortedDates
    this.gasConsumption = this._truncateValues(sortedGasConsumption, 2)
    this.gasConsumptionPrognose = this._truncateValues(sortedPrognose, 2)
    this.gasConsumptionMlPrognose = this._truncateValues(sortedPrognoseMl, 2)
    this.gebKapazitaet = this._truncateValues(sortedGebKapa, 2)
    this.angebot = this._truncateValues(sortedAngebot, 2)
    this.regAngebot = this._truncateValues(sortedRegAngebot, 2)
    this.nomKapazitaet = this._truncateValues(sortedNomKapa, 2)
    this.gasConsumptionPrognoseMass = this._truncateValues(sortedPrognoseMass, 2)
    return html`
      <div ${ref(el => this.drawPlot(el))} id="plot" style="border-radius: 20px; overflow: hidden;"></div>
    `
  }

  // Override createRenderRoot to disable shadow DOM
  createRenderRoot() {
    return this
  }

  drawPlot(plotElement) {
    if (!plotElement || !Plotly) {
      return
    }

    const tempTrace = {
      x: this.dates,
      y: this.temperature,
      name: this.i18n.translate(`gas.${this.source}.temperatur`),
      type: 'bar',
      opacity: 0.3,
      marker: {color: 'rgba(0, 0, 0, 0.3)'},
      yaxis: 'y2'
    }

    const gasTrace = {
      x: this.dates,
      y: this.gasConsumption,
      name: this.i18n.translate(`gas.${this.source}.verbrauch`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'green', width: 2},
      yaxis: 'y1',
    }

    const progVerbrauchTrace = {
      x: this.dates,
      y: this.gasConsumptionPrognose,
      name: this.i18n.translate(`gas.${this.source}.prog.verbrauch`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'red', width: 2},
      yaxis: 'y1',
    }

    const progMlVerbrauchTrace = {
      x: this.dates,
      y: this.gasConsumptionMlPrognose,
      name: this.i18n.translate(`gas.${this.source}.prog.ml.verbrauch`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'orange', width: 2},
      yaxis: 'y1',
      visible: this.gasConsumptionMlPrognoseVisible,
      showlegend: this.gasConsumptionMlPrognoseVisible
    }

    const gebKapazitaetTrace = {
      x: this.dates,
      y: this.gebKapazitaet,
      name: this.i18n.translate(`gas.${this.source}.geb.kapa`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'blue', width: 2},
      yaxis: 'y1',
    }

    const angebotTrace = {
      x: this.dates,
      y: this.angebot,
      name: this.i18n.translate(`gas.${this.source}.angebot`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'rgb(128, 124, 255)', dash: 'dash', width: 1},
      yaxis: 'y1',
    }

    const regAngebotTrace = {
      x: this.dates,
      y: this.regAngebot,
      name: this.i18n.translate(`gas.${this.source}.reg.angebot`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'rgb(128, 124, 255)', dash: 'dot', width: 1},
      yaxis: 'y1',
    }

    const nomKapazitaetTrace = {
      x: this.dates,
      y: this.nomKapazitaet,
      name: this.i18n.translate(`gas.${this.source}.nom.kapa`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'gray', width: 2},
      yaxis: 'y1',
      visible: 'legendonly'
    }

    const progVerbrauchMassTrace = {
      x: this.dates,
      y: this.gasConsumptionPrognoseMass,
      name: this.i18n.translate(`gas.${this.source}.prog.verbrauch.mass`),
      type: GasConsumptionPlot.PLOT_CONFIG.SCATTER_TYPE,
      mode: GasConsumptionPlot.PLOT_CONFIG.LINES_MODE,
      line: {color: 'rgb(255, 124, 128)', dash: 'dash', width: 1},
      yaxis: 'y1'
    }

    const data = [tempTrace, gasTrace, progVerbrauchTrace, gebKapazitaetTrace, nomKapazitaetTrace]

    if (this.source === 'verbrauch') {
      data.splice(3, 0, progMlVerbrauchTrace)
    }

    if (this.source === 'massnahmen') {
      data.splice(4, 0, angebotTrace)
      data.push(progVerbrauchMassTrace)
      if (this.region === 'gaznat') {
        data.splice(5, 0, regAngebotTrace)
      }
    }

    const layout = {
      xaxis: {title: ''},
      yaxis: {title: '', ticksuffix: ' GWh/d'},
      yaxis2: {title: '', showgrid: false, zeroline: false, overlaying: 'y', side: 'right', ticksuffix: ' °C', dtick: 2},
      legend: {orientation: 'h', x: 0.5, xanchor: 'center', y: 1.1},
      margin: {t: 50} // if no title
    }
    const config = {
      responsive: true,
      useResizeHandler: true,
      showLink: false,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso', 'select', 'auto', 'zoom2d', 'pan2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d'],
      displayModeBar: true
    }

    Plotly.newPlot(plotElement, data, layout, config)

    setTimeout(() => {
      Plotly.Plots.resize(plotElement)
    }, 100)
  }

  // Sort dates and all associated data
  _sortData(dates, ...dataArrays) {
    const combined = dates.map((date, i) => [date, ...dataArrays.map(arr => arr[i])]);
    combined.sort((a, b) => new Date(a[0]) - new Date(b[0])); // Sort by dates
    const sortedDates = combined.map(row => row[0]);
    const sortedData = dataArrays.map((_, i) => combined.map(row => row[i + 1]));
    return [sortedDates, ...sortedData];
  }

  _truncateValues(array, decimalPlaces) {
    return array.map(value => !isNaN(value) && value !== 0 ? value.toFixed(decimalPlaces) : NaN);
  }
}
customElements.define('gas-verbrauch-plot', GasConsumptionPlot);
